<template>
  <BaseModal
    :modalTitle="$t('area.lab-equipment.maintenance.action.register')"
    :modalIntro="$t('area.lab-equipment.maintenance.intro')">
    <UiBaseFormVee
      submitOnCtrlEnter
      :errorText="errorText"
      :resetCallback="formResetCallback"
      :onSubmit="onSubmit">
      <div class="mb-6">
        <UiInputSelectVee
          v-if="!labEquipmentID"
          name="labEquipmentID"
          :label="$t('area.lab-equipment.maintenance.field.equipment')"
          :unselectedText="
            $t('area.lab-equipment.maintenance.placeholder.select-equipment')
          "
          :options="equipmentOptions"
          :rules="['required']"
          isRequired
          v-model="form.labEquipmentID"
          @update:model-value="onEquipmentChange" />

        <UiInputDatePickerVee
          showAsRequired
          name="date"
          :label="$t('area.lab-equipment.maintenance.field.service-date')"
          :placeholder="
            $t('area.lab-equipment.maintenance.placeholder.select-date')
          "
          :rules="['required']"
          isRequired
          v-model="formDateAsDate"
          :dateFormat="(date: Date) => $d(date, 'shortMonthDate')"
          @update:model-value="resetValidationMessages" />

        <UiInputDatePickerVee
          name="nextMaintenanceDate"
          :label="$t('area.lab-equipment.maintenance.field.next-service-date')"
          :placeholder="
            $t('area.lab-equipment.maintenance.placeholder.select-date')
          "
          :dateFormat="(date: Date) => $d(date, 'shortMonthDate')"
          v-model="formnextMaintenanceDateAsDate"
          @update:model-value="resetValidationMessages" />
      </div>

      <template #buttons>
        <UiBaseButton
          :text="$t('action.save')"
          :isLoading="isLoading"
          type="submit" />
      </template>
    </UiBaseFormVee>
  </BaseModal>
</template>

<script lang="ts">
import { parseDateStr, formatDateToStr } from '@/globals/typescript/utils/dates'
import { getErrorMessage } from '@/globals/typescript/utils/errorMessages'
import { useToast } from '@/globals/typescript/utils/toast'
import { useLabEquipmentStore } from '@/stores/labEquipmentStore'
import { mapActions, mapState } from 'pinia'
import { useModalStore } from '@/stores/modalStore'
import { useUnsavedChangesGuard } from '@/composables/useUnsavedChangesGuard'

function createForm() {
  return {
    labEquipmentID: '',
    date: '',
    nextMaintenanceDate: '',
  }
}

export default {
  name: 'AddLabEquipmentMaintenanceModal',

  emits: ['close'],

  props: {
    labEquipmentID: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      unsavedGuard: useUnsavedChangesGuard(),
      form: createForm(),
      isLoading: false,
      errorText: '',
    }
  },

  computed: {
    ...mapState(useLabEquipmentStore, ['labEquipment']),

    equipmentOptions(): { value: string; text: string }[] {
      return this.labEquipment.map((equipment) => ({
        value: equipment.id,
        text: `${equipment.name} (${equipment.type})`,
      }))
    },

    formDateAsDate: {
      get(): Date | null {
        return parseDateStr(this.form.date)
      },
      set(val: Date | null) {
        this.form.date = val ? formatDateToStr(val) : ''
      },
    },

    formnextMaintenanceDateAsDate: {
      get(): Date | null {
        return parseDateStr(this.form.nextMaintenanceDate)
      },
      set(val: Date | null) {
        this.form.nextMaintenanceDate = val ? formatDateToStr(val) : ''
      },
    },
  },

  methods: {
    ...mapActions(useLabEquipmentStore, ['addLabEquipmentMaintenance']),

    async onSubmit() {
      this.isLoading = true
      this.resetValidationMessages()

      try {
        await this.addLabEquipmentMaintenance({
          labEquipmentID: this.form.labEquipmentID,
          date: this.form.date,
          nextMaintenanceDate: this.form.nextMaintenanceDate || null,
        })

        this.unsavedGuard.takeSnapshot()
        useToast().success(this.$t('W: Vedligehold registreret'))
        this.$emit('close')
      } catch (error) {
        const code = error instanceof Error ? error.message : ''
        this.errorText = getErrorMessage(code)
        useToast().error(this.$t('W: Kunne ikke registrere vedligehold'))
      } finally {
        this.isLoading = false
      }
    },

    formResetCallback(): boolean {
      if (this.errorText) {
        return false
      }

      this.form = createForm()
      return true
    },

    onEquipmentChange(labEquipmentID: string) {
      this.resetValidationMessages()
      const equipment = this.labEquipment.find((e) => e.id === labEquipmentID)
      this.form.nextMaintenanceDate = equipment?.nextMaintenanceDate ?? ''
    },

    resetValidationMessages() {
      this.errorText = ''
    },
  },

  created() {
    if (this.labEquipmentID) {
      this.form.labEquipmentID = this.labEquipmentID
      const equipment = this.labEquipment.find(
        (e) => e.id === this.labEquipmentID
      )
      this.form.nextMaintenanceDate = equipment?.nextMaintenanceDate ?? ''
    }
    // The modal is always the top entry at the moment it is created, so this
    // is its own ModalEntry id — it lets ModalHost tell "the modal I am about
    // to close is dirty" apart from "something underneath it is dirty".
    this.unsavedGuard.register({
      snapshot: () => ({ ...this.form }),
      modalId: useModalStore().top?.id,
    })
  },

  beforeUnmount() {
    this.unsavedGuard.unregister()
  },
}
</script>

